import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import Box from '@mui/material/Box'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import PersonIcon from '@mui/icons-material/Person'
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore'
import Database from '../../types/Database'

interface Participant {
  id: string
  displayName: string
  handle: string | null
  isHost: boolean
}

interface DiveParticipantsProps {
  diveId: string
  hostId: string
}

const DiveParticipants = ({ diveId, hostId }: DiveParticipantsProps) => {
  const [participants, setParticipants] = useState<Participant[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const fetch = async () => {
      const db = getFirestore()

      // Get approved participants
      const joinRequestsRef = collection(db, 'joinRequests')
      const approvedQuery = query(
        joinRequestsRef,
        where('diveId', '==', diveId),
        where('status', '==', 'approved')
      )
      const approvedSnap = await getDocs(approvedQuery)

      const participantIds = new Set<string>()
      approvedSnap.forEach(docSnap => {
        const data = docSnap.data() as Omit<Database.JoinRequest, 'id'>
        participantIds.add(data.requesterId)
      })

      // Always include host
      participantIds.add(hostId)

      // Resolve user docs
      const resolved: Participant[] = await Promise.all(
        Array.from(participantIds).map(async uid => {
          const userRef = doc(db, 'users', uid)
          const userSnap = await getDoc(userRef)
          if (userSnap.exists()) {
            const data = userSnap.data() as Omit<Database.User, 'id'>
            return {
              id: uid,
              displayName: data.displayName,
              handle: data.handle,
              isHost: uid === hostId
            }
          }
          return {
            id: uid,
            displayName: 'Unknown',
            handle: null,
            isHost: uid === hostId
          }
        })
      )

      // Host first
      resolved.sort((a, b) => (a.isHost ? -1 : b.isHost ? 1 : 0))

      if (!cancelled) {
        setParticipants(resolved)
        setLoading(false)
      }
    }

    fetch().catch(() => {
      if (!cancelled) setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [diveId, hostId])

  if (loading || participants.length === 0) return null

  return (
    <Box>
      <Typography variant='subtitle2' fontWeight='bold' sx={{ mb: 1 }}>
        Divers
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {participants.map(p => {
          const initials = p.displayName
            .split(' ')
            .map(w => w[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()

          return (
            <Box
              key={p.id}
              component={Link}
              to={`/profile/${p.id}`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
                width: 64
              }}
            >
              <Avatar sx={{ width: 40, height: 40, fontSize: 14, mb: 0.5 }}>
                {initials || <PersonIcon />}
              </Avatar>
              <Typography
                variant='caption'
                noWrap
                sx={{ maxWidth: 64, textAlign: 'center' }}
              >
                {p.handle ? `@${p.handle}` : p.displayName.split(' ')[0]}
              </Typography>
              {p.isHost && (
                <Chip
                  label='Guide'
                  size='small'
                  color='primary'
                  variant='outlined'
                  sx={{ height: 16, fontSize: 10, mt: 0.25 }}
                />
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default DiveParticipants
